/**
 * A module's playlist, as the order it is written in.
 *
 * The playlist is an ordered list of capsule records, and *ordered* is the whole
 * of what a module adds to the capsules it gathers: a course has a sequence, and
 * the player follows it. So the edits that matter are the ones that change the
 * sequence — put one in, take one out, move one along — and they are here, pure
 * and cheap to test, rather than spread through a studio's event handlers where
 * a dropped item or a duplicate would only be noticed by a learner.
 *
 * Every function returns a **new** list and never mutates the one it was given.
 * That is not decoration: the studio holds the playlist in React state and writes
 * it to the record, and an edit that mutated in place would leave the state and
 * the save agreeing by luck.
 */
import {type ModuleItem} from '#/lib/modulePlaylist'

/**
 * The playlist a set of records makes, in the order they were given.
 *
 * Both addresses travel with each place — the capsule record and the post it was
 * published as — because the record is what the gate and the coin total read and
 * the post is what the carousel draws (see `ModuleItem`). A record with no post
 * is a draft, and the item says so by having none.
 */
export function moduleItemsOf(
  records: readonly {uri: string; cid?: string; postUri?: string}[],
): ModuleItem[] {
  return records.map(record => ({
    uri: record.uri,
    cid: record.cid,
    ...(record.postUri ? {postUri: record.postUri} : null),
  }))
}

/** Whether a capsule is already in the playlist. */
export function hasModuleItem(
  items: readonly ModuleItem[],
  uri: string,
): boolean {
  return items.some(item => item.uri === uri)
}

/**
 * The playlist with a capsule added to its end.
 *
 * Adding is *idempotent*: a capsule already in the list is not added twice. The
 * caller may well have checked — the picker does — but the two ways a capsule
 * arrives in a module (chosen in the picker, or written inside it and handed back
 * by the studio) both end here, and the second one is a return path where a
 * double-add is exactly the kind of thing that happens. One capsule per place is
 * a rule that can be enforced where it is written.
 */
export function appendModuleItem(
  items: readonly ModuleItem[],
  item: ModuleItem,
): ModuleItem[] {
  if (hasModuleItem(items, item.uri)) return [...items]
  return [...items, item]
}

/** The playlist without a capsule. */
export function removeModuleItem(
  items: readonly ModuleItem[],
  uri: string,
): ModuleItem[] {
  return items.filter(item => item.uri !== uri)
}

/**
 * The playlist with a capsule moved one place earlier or later.
 *
 * `delta` is the number of places to move, and a move off either end is a no-op
 * rather than an error: the studio draws an arrow that is disabled at the end of
 * the row, and the *list* still has to survive being asked to move the first item
 * back — a dropped tap, a racing re-render — without reordering itself from the
 * end. Swapping in place is what keeps every other position exactly where it was.
 */
export function moveModuleItem(
  items: readonly ModuleItem[],
  uri: string,
  delta: number,
): ModuleItem[] {
  const from = items.findIndex(item => item.uri === uri)
  if (from < 0) return [...items]
  const to = from + delta
  if (to < 0 || to >= items.length) return [...items]
  const next = [...items]
  ;[next[from], next[to]] = [next[to], next[from]]
  return next
}
